import { Tabs } from '@nimbus-ds/components';
import { Prompt, useParams } from 'react-router';
import useLayoutScroll from 'App/components/AppLayout/useLayoutScroll';
import { NEW_ADMIN_WHATSAPP_NOTIFICATIONS } from 'App/featuresFlags';
import HelpLink from 'App/HelpLink';
import { useHasTags, useNavegate } from 'App/hooks';
import { toStatusType } from 'App/rtk';
import { HeaderContent, HeaderTop, IonPageStratus } from 'commons/components';
import { HELP_LINKS_NOTIFICATIONS } from 'commons/constants';
import { useForm, useToastStatus, useTranslationLanguage } from 'commons/hooks';
import { useGetAllLanguages } from 'domains/Auth/hooks';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import {
  EditNotificationPageError,
  EditNotificationPageSkeleton,
  EmailNotificationTab,
  WhatsappNotificationTab,
} from './components';
import { NOTIFICATION_DETAILS_INITIAL_VALUES } from './constants';
import { validationSchema } from './validationSchema';
import {
  useGetEmailQuery,
  useGetWhatsappNotificationListQuery,
  useUpdateEmailMutation,
} from '../../notificationsApi';
import './EditNotificationPage.scss';

function EditNotificationPage() {
  const t = useTranslationConfigurations('notifications.editNotificationPage');
  const language = useTranslationLanguage();
  const languages = useGetAllLanguages();
  const { id } = useParams<{ id: string }>();
  const { goBack } = useNavegate();
  const { scrollToTop } = useLayoutScroll();
  const [hasWhatsappNotificationsTag] = useHasTags([
    NEW_ADMIN_WHATSAPP_NOTIFICATIONS,
  ]);

  const {
    data: emailNotification,
    isLoading,
    isError,
    refetch,
  } = useGetEmailQuery(id);

  const [updateEmail, { status }] = useUpdateEmailMutation();

  const { data: whatsappNotifications } = useGetWhatsappNotificationListQuery(
    undefined,
    { skip: !hasWhatsappNotificationsTag },
  );

  const { values, isDirty, setFieldValue, handleOnSubmit, errors } = useForm({
    validationSchema: validationSchema(languages),
    initialValues: emailNotification || NOTIFICATION_DETAILS_INITIAL_VALUES,
    onSubmit: (data) =>
      updateEmail({
        id,
        payload: {
          data,
          initialValues:
            emailNotification || NOTIFICATION_DETAILS_INITIAL_VALUES,
        },
      }),
  });

  const updateStatus = toStatusType(status);
  useToastStatus({
    status: updateStatus,
    success: t('toast.success'),
    error: t('toast.error'),
    onSuccess: goBack,
    onError: scrollToTop,
  });

  const hasError = languages.some((lang) => !!errors[`${lang}.templateHtml`]);

  const saveEmailAction = {
    appearance: 'primary' as const,
    onClick: handleOnSubmit,
    children: t('save'),
    disabled: !isDirty,
  };

  const isOrderPickupEmail = emailNotification?.type === 'orderreadyforpickup';

  const whatsappNotification = whatsappNotifications?.items?.find(
    (notification) => notification.type === emailNotification?.type,
  );

  const whatsappAvailable = !!whatsappNotification?.whatsapp?.available;
  const whatsappEnabled = !!whatsappNotification?.whatsapp?.enabled;

  const shouldRenderTabs =
    !isLoading &&
    !isError &&
    emailNotification &&
    hasWhatsappNotificationsTag &&
    whatsappNotification &&
    whatsappAvailable;

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        <HeaderTop
          navigation={{ onClick: goBack }}
          mainAction={saveEmailAction}
        />
      }
      headerContent={
        <HeaderContent
          title={!isLoading ? emailNotification?.title : 'skeleton'}
          subtitle={!isLoading ? emailNotification?.description : 'skeleton'}
          labels={
            isOrderPickupEmail
              ? [
                  !emailNotification
                    ? 'skeleton'
                    : {
                        id: 'edit-notification-active-label',
                        appearance: emailNotification?.active
                          ? 'success'
                          : 'default',
                        label: t(
                          `${
                            emailNotification?.active
                              ? 'enabledTag'
                              : 'disabledTag'
                          }`,
                        ),
                      },
                ]
              : []
          }
        />
      }
    >
      <Prompt
        when={updateStatus === 'idle' && isDirty}
        message={t('exitPrompt')}
      />
      {isLoading && <EditNotificationPageSkeleton />}
      {isError && <EditNotificationPageError refetch={refetch} />}

      {!isLoading &&
        !isError &&
        emailNotification &&
        (!hasWhatsappNotificationsTag || !whatsappAvailable) && (
          <EmailNotificationTab
            email={emailNotification}
            values={values}
            setFieldValue={setFieldValue}
            handleOnSubmit={handleOnSubmit}
            updateStatus={updateStatus}
            hasError={hasError}
            isDirty={isDirty}
            isOrderPickupEmail={isOrderPickupEmail}
          />
        )}

      {shouldRenderTabs && (
        <Tabs>
          <Tabs.Item label={t('tabs.email')}>
            <EmailNotificationTab
              email={emailNotification}
              values={values}
              setFieldValue={setFieldValue}
              handleOnSubmit={handleOnSubmit}
              updateStatus={updateStatus}
              hasError={hasError}
              isDirty={isDirty}
              isOrderPickupEmail={isOrderPickupEmail}
            />
          </Tabs.Item>
          <Tabs.Item label={t('tabs.whatsapp')}>
            <WhatsappNotificationTab
              notificationId={whatsappNotification.id}
              isEnabled={whatsappEnabled}
              emailTitle={emailNotification.title}
              isToggleBlocked={isOrderPickupEmail && !emailNotification.active}
            />
          </Tabs.Item>
        </Tabs>
      )}
      <HelpLink
        title={t('helpLink.title')}
        previousValue=""
        currentViewTracking={t('helpLink.amplitudeName')}
        linkURL={HELP_LINKS_NOTIFICATIONS[language]}
        hasDisclaimer={false}
        icon="both"
      />
    </IonPageStratus>
  );
}

export default EditNotificationPage;
